/**
 * Preview dev-only de Reportes de Tesorería (Sprint 20 SR-07).
 *
 * Gateado con import.meta.env.DEV en App.tsx — inaccesible en producción.
 * Muestra el catálogo de tiles y, al "Ver", el detalle con mock data.
 */
import { useState } from 'react';
import {
  CatalogoReportesView,
  RepHeader,
  MorosidadDetalle,
  IngresosGastosDetalle,
  GastosCategoriaDetalle,
  CompensacionesDetalle,
  ConciliacionDetalle,
  ProveedoresDetalle,
  CierreMensualDetalle,
  ResumenAnualDetalle,
  FlujoCajaDetalle,
  FondoReservaDetalle,
  ProyeccionDetalle,
  RecaudacionDetalle,
  CierreSemestralDetalleView,
} from '../tesoreria/Reportes';
import { InformeMensualVecinos } from '../../components/tesoreria/InformeMensualVecinos';
import { RecordatorioSemestral } from '../../components/tesoreria/RecordatorioSemestral';
import type {
  InformeMensualVecinosResponse,
  ModoEnvioRecordatorio,
  RecordatorioSemestralResponse,
} from '../../lib/api/reportes-tesoreria';
import {
  RECORDATORIO_ASUNTO_MOCK,
  RECORDATORIO_HTML_MOCK,
} from './recordatorioSemestralMock';
import type { CompensacionConDetalle } from '../../lib/api/types';
import type { EstadoCierres } from '../../lib/api/cierres-semestrales';
import { CIERRES_MOCK, DETALLES_MOCK } from './cierreSemestralMock';

const MOROSIDAD_MOCK = {
  total_mora: 312000,
  casas_morosas: 5,
  total_casas: 71,
  cartera_semestral: 2130000,
  deuda_promedio: 62400,
  pct_cartera: 0.1465,
  buckets: [
    { id: 'leve', label: '1 mes', descripcion: 'Atraso reciente', color: '#ffb775', casas: 2, monto: 60000 },
    { id: 'medio', label: '2 – 3 meses', descripcion: 'Requiere gestión', color: '#ff9d5c', casas: 2, monto: 102000 },
    { id: 'critico', label: '4+ meses', descripcion: 'Crítico · cobranza/legal', color: '#ff7a7a', casas: 1, monto: 150000 },
  ],
  evolucion: [
    { anio: 2025, mes: 12, monto: 388000 },
    { anio: 2026, mes: 1, monto: 412000 },
    { anio: 2026, mes: 2, monto: 437000 },
    { anio: 2026, mes: 3, monto: 285000 },
    { anio: 2026, mes: 4, monto: 264000 },
    { anio: 2026, mes: 5, monto: 312000 },
  ],
  casas: [
    { lote_id: 'a1', identificador: '6921', titular_nombre: 'Ana Pérez', meses_mora: 5, ultimo_pago: '2025-12-10', saldo_a_hoy: 150000 },
    { lote_id: 'a2', identificador: '6934', titular_nombre: 'Luis Soto', meses_mora: 3, ultimo_pago: '2026-02-15', saldo_a_hoy: 54000 },
    { lote_id: 'a3', identificador: '6947', titular_nombre: 'María Rojas', meses_mora: 2, ultimo_pago: '2026-03-01', saldo_a_hoy: 48000 },
    { lote_id: 'a4', identificador: '6951', titular_nombre: 'Pedro Rojas', meses_mora: 1, ultimo_pago: '2026-04-20', saldo_a_hoy: 30000 },
    { lote_id: 'a5', identificador: '6963', titular_nombre: 'Carla Vega', meses_mora: 1, ultimo_pago: '2026-04-05', saldo_a_hoy: 30000 },
  ],
};

const INGRESOS_GASTOS_MOCK = {
  anio: 2026,
  meses: Array.from({ length: 12 }, (_, i) => ({
    mes: `2026-${String(i + 1).padStart(2, '0')}-01`,
    recaudacion: 150000 + i * 5000,
    gastos: 120000 + i * 3000,
    es_futuro: i >= 6,
    es_parcial: i === 5,
  })),
};

const GASTOS_CATEGORIA_MOCK = {
  mes: '2026-06-01',
  total: 480000,
  items: [
    {
      categoria_id: 'c1', categoria_nombre: 'Seguridad', categoria_color: '#ffb775',
      monto: 300000,
      hijos: [
        { categoria_id: 'c1a', categoria_nombre: 'Guardias', categoria_color: '#ffb775', monto: 250000 },
        { categoria_id: 'c1b', categoria_nombre: 'Cámaras', categoria_color: '#ffb775', monto: 50000 },
      ],
    },
    {
      categoria_id: 'c2', categoria_nombre: 'Mantención', categoria_color: '#5fd9b1',
      monto: 180000, hijos: [],
    },
  ],
};

// Mock parcial (dev-only): el detalle solo lee estos campos.
const COMPENSACIONES_MOCK = [
  {
    id: 'k1', lote_identificador: '6921', vecino_nombre: 'Ana Pérez',
    categoria_nombre: 'Citofonía', monto: 30000, consumido: 30000, activa: false,
  },
  {
    id: 'k2', lote_identificador: '6934', vecino_nombre: 'Luis Soto',
    categoria_nombre: 'Áreas verdes', monto: 60000, consumido: 20000, activa: true,
  },
] as unknown as CompensacionConDetalle[];

const RECAUDACION_MOCK = {
  anio: 2026,
  total_recaudado_barrio: 125000,
  total_terceros: 10000,
  total_casas: 71,
  meses_transcurridos: 5,
  esperado: 355000,
  cumplimiento: 0.352,
  casas_al_dia: 2,
  casas: [
    { lote_id: 'a1', identificador: '6921', sector: 'Oriente', titular_nombre: 'Ana Pérez', estado: 'al_dia', cuotas_pagadas: 5, recaudado_barrio: 60000, aportado_terceros: 0 },
    { lote_id: 'a2', identificador: '6934', sector: 'Poniente', titular_nombre: 'Luis Soto', estado: 'compensacion', cuotas_pagadas: 3, recaudado_barrio: 35000, aportado_terceros: 10000 },
    { lote_id: 'a3', identificador: '6947', sector: 'Oriente', titular_nombre: 'María Rojas', estado: 'atrasada', cuotas_pagadas: 2, recaudado_barrio: 30000, aportado_terceros: 0 },
    { lote_id: 'a4', identificador: '6951', sector: 'Poniente', titular_nombre: 'Pedro Rojas', estado: 'atrasada', cuotas_pagadas: 0, recaudado_barrio: 0, aportado_terceros: 0 },
  ],
};

const CONCILIACION_MOCK = {
  tiene_cuadre: true,
  ultimo_cuadre_fecha: '2026-06-15',
  saldo_banco: 2485000,
  saldo_calculado: 2480000,
  diferencia: 5000,
  capital_dap_vigente: 3000000,
  posicion_total: 5485000,
  saldo_calculado_hoy: 2610000,
  movimientos_desde_cuadre: { ingresos: 180000, gastos: 50000, traspasos_dap: 0 },
  daps_vigentes: [
    {
      id: 'd1', banco: 'Banco de Chile', capital: 3000000,
      fecha_apertura: '2026-05-01', fecha_vencimiento: '2026-07-30',
      tasa_bps: 65, folio: 'DAP-1042',
    },
  ],
  historico: [
    { fecha_registro: '2026-06-15', hora_registro: '10:30:00', saldo_banco: 2485000, saldo_calculado: 2480000, diferencia: 5000, notas: 'Cuadre quincenal' },
    { fecha_registro: '2026-05-31', hora_registro: '18:00:00', saldo_banco: 2305000, saldo_calculado: 2305000, diferencia: 0, notas: null },
    { fecha_registro: '2026-05-15', hora_registro: '09:15:00', saldo_banco: 2150000, saldo_calculado: 2148000, diferencia: 2000, notas: null },
  ],
};

const PROVEEDORES_MOCK = {
  anio: 2026,
  total_gastado: 4800000,
  n_proveedores: 6,
  concentracion_top3: 0.78,
  ticket_promedio: 160000,
  recurrentes: { n: 2, monto: 3600000 },
  puntuales: { n: 4, monto: 1200000 },
  proveedores: [
    { proveedor: 'Seguridad Sur Ltda.', total: 2400000, pct: 0.5, n_operaciones: 6, meses_activos: 6, categoria_predominante: 'Seguridad', ticket_promedio: 400000, recurrente: true },
    { proveedor: 'Jardines Verdes', total: 1200000, pct: 0.25, n_operaciones: 6, meses_activos: 6, categoria_predominante: 'Áreas verdes', ticket_promedio: 200000, recurrente: true },
    { proveedor: 'Ferretería El Tornillo', total: 480000, pct: 0.1, n_operaciones: 3, meses_activos: 2, categoria_predominante: 'Mantención', ticket_promedio: 160000, recurrente: false },
    { proveedor: 'Pinturas RapidColor', total: 360000, pct: 0.075, n_operaciones: 1, meses_activos: 1, categoria_predominante: 'Mantención', ticket_promedio: 360000, recurrente: false },
    { proveedor: 'Electricidad Norte', total: 240000, pct: 0.05, n_operaciones: 2, meses_activos: 1, categoria_predominante: 'Mantención', ticket_promedio: 120000, recurrente: false },
    { proveedor: 'Aseo Total', total: 120000, pct: 0.025, n_operaciones: 1, meses_activos: 1, categoria_predominante: 'Aseo', ticket_promedio: 120000, recurrente: false },
  ],
};

const CIERRE_MENSUAL_MOCK = {
  anio: 2026,
  mes: 4,
  periodo: 'Abril 2026',
  ingresos: 7120000,
  gastos: 5840000,
  resultado: 1280000,
  trend_ingresos: 2.0,
  trend_gastos: -5.0,
  trend_resultado: 89.5,
  saldo_cierre: 18120000,
  saldo_apertura: 16840000,
  saldo_cierre_fecha: '2026-08-27',
  saldo_apertura_fecha: '2026-07-27',
  casas_que_pagaron: 58,
  total_casas: 71,
  pagos_del_mes: 58,
  nuevos_morosos: 3,
  compensaciones_n: 2,
  compensaciones_monto: 10000,
  ingresos_por_tipo: [
    { tipo: 'Cuotas', monto: 6200000, color: '#5fd9b1' },
    { tipo: 'Venta de controles', monto: 350000, color: '#5ab4d1' },
    { tipo: 'Multas', monto: 200000, color: '#ff7a7a' },
    { tipo: 'Venta de llaveros', monto: 150000, color: '#5ad1c4' },
    { tipo: 'Intereses', monto: 130000, color: '#7aa2ff' },
    { tipo: 'Excedentes', monto: 90000, color: '#ffdb5a' },
  ],
  gastos_por_categoria: [
    { tipo: 'Seguridad', monto: 2150000, color: '#7ab8ff' },
    { tipo: 'Mantención', monto: 1310000, color: '#5fd9b1' },
    { tipo: 'Administración', monto: 920000, color: '#b89bff' },
    { tipo: 'Servicios básicos', monto: 680000, color: '#ffb775' },
    { tipo: 'Imprevistos', monto: 780000, color: '#ff9d5c' },
  ],
};

const RESUMEN_ANUAL_MOCK = {
  anio: 2025,
  periodo: 'Ejercicio 2025',
  es_anio_en_curso: false,
  ingresos: 77996000,
  gastos: 64200000,
  resultado: 13796000,
  saldo_cierre: 18120000,
  ingresos_por_tipo: [
    { tipo: 'Cuotas', monto: 74000000, color: '#5fd9b1' },
    { tipo: 'Venta de controles', monto: 1500000, color: '#5ab4d1' },
    { tipo: 'Multas', monto: 1240000, color: '#ff7a7a' },
    { tipo: 'Venta de llaveros', monto: 800000, color: '#5ad1c4' },
    { tipo: 'Excedentes', monto: 300000, color: '#ffdb5a' },
    { tipo: 'Intereses', monto: 156000, color: '#7aa2ff' },
  ],
  gastos_por_categoria: [
    { tipo: 'Seguridad', monto: 28800000, color: '#7ab8ff' },
    { tipo: 'Mantención', monto: 15600000, color: '#5fd9b1' },
    { tipo: 'Administración', monto: 11000000, color: '#b89bff' },
    { tipo: 'Servicios básicos', monto: 8200000, color: '#ffb775' },
    { tipo: 'Imprevistos', monto: 600000, color: '#ff9d5c' },
  ],
};

const FLUJO_CAJA_MOCK = {
  anio: 2025,
  saldo_inicial: 9500000,
  saldo_inicial_disponible: true,
  saldo_final: 18120000,
  saldo_minimo: 9500000,
  saldo_maximo: 18120000,
  meses: [
    { mes: 1, entradas: 6800000, salidas: 5600000, neto: 1200000, saldo: 10700000 },
    { mes: 2, entradas: 6800000, salidas: 5400000, neto: 1400000, saldo: 12100000 },
    { mes: 3, entradas: 6800000, salidas: 5200000, neto: 1600000, saldo: 13700000 },
    { mes: 4, entradas: 7120000, salidas: 5840000, neto: 1280000, saldo: 14980000 },
    { mes: 5, entradas: 6800000, salidas: 5500000, neto: 1300000, saldo: 16280000 },
    { mes: 6, entradas: 6800000, salidas: 4960000, neto: 1840000, saldo: 18120000 },
  ],
};

const FONDO_RESERVA_MOCK = {
  anio: 2025,
  saldo_fondo: 24000000,
  meta: 32100000,
  pct_meta: 0.7477,
  gasto_mensual_promedio: 5350000,
  meses_cobertura: 4.5,
  meses: [
    { mes: 1, saldo: 18000000, pct_meta: 0.5607 },
    { mes: 2, saldo: 18000000, pct_meta: 0.5607 },
    { mes: 3, saldo: 21000000, pct_meta: 0.6542 },
    { mes: 4, saldo: 21000000, pct_meta: 0.6542 },
    { mes: 5, saldo: 24000000, pct_meta: 0.7477 },
    { mes: 6, saldo: 24000000, pct_meta: 0.7477 },
  ],
};

// Proyección con las dos líneas (2026-09-04). Números del orden de los reales
// para que la brecha se vea como se va a ver: al ritmo actual el barrio apenas
// empata, y eso es justamente lo que el reporte viene a mostrar.
const PROYECCION_MOCK = {
  saldo_actual: 2084447,
  saldo_actual_disponible: true,
  ingreso_mensual_promedio: 355000,
  ingreso_mensual_al_ritmo_actual: 292378,
  gasto_mensual_promedio: 286460,
  ingreso_proyectado_total: 4260000,
  gasto_proyectado_total: 3437520,
  saldo_proyectado: 2906927,
  saldo_proyectado_al_ritmo_actual: 2155463,
  emision_anual: 4260000,
  tasa_cobranza: 0.824,
  tasa_cobranza_emitido: 3090000,
  tasa_cobranza_cobrado: 2545000,
  tasa_cobranza_periodos: 2,
  gasto_meses_ventana: 12,
  gasto_usa_historico: true,
  meses: Array.from({ length: 12 }, (_, i) => {
    const mes = ((8 + i) % 12) + 1;
    const anio = 2026 + Math.floor((8 + i) / 12);
    const neto = 355000 - 286460;
    const netoRitmo = 292378 - 286460;
    return {
      anio,
      mes,
      ingreso: 355000,
      ingreso_al_ritmo_actual: 292378,
      gasto: 286460,
      neto,
      neto_al_ritmo_actual: netoRitmo,
      saldo: 2084447 + neto * (i + 1),
      saldo_al_ritmo_actual: 2084447 + netoRitmo * (i + 1),
    };
  }),
};


// Informe mensual a los vecinos (2026-09-03). Casas inventadas (9xxx) y cero
// PII: el preview va al repo y los datos de vecinos no.
const INFORME_VECINOS_MOCK: InformeMensualVecinosResponse = {
  anio: 2026,
  mes: 8,
  periodo: 'Agosto 2026',
  asunto: 'Cierre de Agosto 2026 — Barrio Calbucura',
  saldo_fecha: '2026-08-27',
  saldo_fecha_apertura: '2026-07-27',
  cierre: {
    ...CIERRE_MENSUAL_MOCK,
    ingresos: 353353,
    gastos: 267980,
    resultado: 85373,
    trend_ingresos: 12,
    trend_gastos: -8,
    saldo_cierre: 1811447,
    saldo_apertura: 1773427,
    saldo_cierre_fecha: '2026-08-27',
    saldo_apertura_fecha: '2026-07-27',
    nuevos_morosos: -12,
    ingresos_por_tipo: [
      { tipo: 'Cuotas', monto: 280000, color: '#5fd9b1' },
      { tipo: 'Venta de recursos', monto: 69000, color: '#7ab8ff' },
      { tipo: 'Intereses', monto: 4353, color: '#b89bff' },
    ],
    gastos_por_categoria: [
      { tipo: 'Plaza', monto: 130000, color: '#ffb775' },
      { tipo: 'Recursos', monto: 118000, color: '#b89bff' },
      { tipo: 'Internet', monto: 19980, color: '#6fd3e0' },
    ],
  },
  proyeccion: {
    desde: '2025-09',
    hasta: '2026-08',
    meses_ventana: 12,
    total: 3437528,
    promedio: 286460,
    mes_minimo: 120000,
    mes_maximo: 976010,
    meses_sin_datos: 0,
    usa_historico: true,
    // La serie mensual ya no alimenta ningún bloque del correo (la sección
    // anual se quitó el 2026-09-03); se deja vacía.
    meses: [],
  },
  cobertura_meses: 6.3,
  notas: [
    'Al cierre del mes 34 casas tenían cuotas pendientes, por un total de $975.000.',
    'El mes cerró con un superávit de $85.373: ingresó más de lo que se gastó.',
    '12 casas salieron de mora respecto del mes anterior.',
    'El mayor gasto del mes fue Plaza ($130.000).',
  ],
  // HTML generado por el backend con estos mismos datos: el preview muestra el
  // correo REAL y no una maqueta aparte que podría divergir del que se envía.
  html:
    '<!DOCTYPE html>\n' +
    '<html lang="es">\n' +
    '<head>\n' +
    '  <meta charset="UTF-8">\n' +
    '  <title>Cierre de Agosto 2026 — Barrio Calbucura</title>\n' +
    '</head>\n' +
    '<body style="margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;background-color:#f5f5f5;color:#222;">\n' +
    '  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#f5f5f5;padding:24px 0;">\n' +
    '    <tr>\n' +
    '      <td align="center">\n' +
    '        <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);">\n' +
    '          <tr>\n' +
    '            <td style="background-color:#000000;padding:24px 32px;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:0.5px;">\n' +
    '              <span style="color:#FF7A00;">Barrio</span> Calbucura\n' +
    '            </td>\n' +
    '          </tr>\n' +
    '          <tr>\n' +
    '            <td style="padding:32px;font-size:15px;line-height:1.6;color:#222;">\n' +
    '              <p style="margin:0 0 16px 0;font-weight:600;">Estimado [nombre del titular], Casa [número]:</p>\n' +
    '              <p style="margin:0 0 16px 0;">Este es el informe de cierre de <strong>Agosto 2026</strong> del Barrio Calbucura. Lo enviamos todos los meses para que sepas en qué se está usando la plata del barrio.</p>\n' +
    '              <h2 style="margin:26px 0 2px 0;font-size:16px;font-weight:700;">El mes en números</h2><p style="margin:0 0 10px 0;font-size:13px;color:#666;">Con la variación respecto del mes anterior.</p><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="padding:9px 0;border-bottom:1px solid #e5e5e5;font-size:14px;">Ingresos del mes</td><td align="right" style="padding:9px 0;border-bottom:1px solid #e5e5e5;font-size:14px;white-space:nowrap;">$353.353<span style="font-size:12px;color:#1e7d54;white-space:nowrap;"> +12%</span></td></tr><tr><td style="padding:9px 0;border-bottom:1px solid #e5e5e5;font-size:14px;">Gastos del mes</td><td align="right" style="padding:9px 0;border-bottom:1px solid #e5e5e5;font-size:14px;white-space:nowrap;">$267.980<span style="font-size:12px;color:#1e7d54;white-space:nowrap;"> −8%</span></td></tr><tr><td style="padding:9px 0;font-size:14px;">Resultado del mes</td><td align="right" style="padding:9px 0;font-size:14px;font-weight:700;white-space:nowrap;">$85.373</td></tr></table><div style="margin:20px 0 0 0;padding:13px 9px;background-color:#000000;border-radius:6px;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td width="34%" valign="top" style="padding:0 5px;"><div style="font-size:9px;letter-spacing:0.05em;text-transform:uppercase;color:#FF7A00;line-height:1.3;">Saldo al 27 de julio</div><div style="font-size:14px;font-weight:700;color:#ffffff;margin-top:2px;white-space:nowrap;">$1.773.427</div></td><td width="33%" valign="top" style="padding:0 5px;"><div style="font-size:9px;letter-spacing:0.05em;text-transform:uppercase;color:#FF7A00;line-height:1.3;">Saldo al 27 de agosto</div><div style="font-size:14px;font-weight:700;color:#ffffff;margin-top:2px;white-space:nowrap;">$1.811.447</div></td><td width="33%" valign="top" style="padding:0 5px;"><div style="font-size:9px;letter-spacing:0.05em;text-transform:uppercase;color:#FF7A00;line-height:1.3;">Alcanza para</div><div style="font-size:14px;font-weight:700;color:#ffffff;margin-top:2px;white-space:nowrap;"><span style="color:#FF7A00;">&#9650;</span> 6,3 meses</div></td></tr></table><div style="margin-top:10px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.15);font-size:11px;line-height:1.5;color:rgba(255,255,255,0.7);">Con un gasto promedio de <strong style="color:#ffffff;">$286.460</strong> al mes, que es lo que ha costado mantener el barrio en los últimos 12 meses.</div></div><h2 style="margin:26px 0 2px 0;font-size:16px;font-weight:700;">En qué se gastó</h2><p style="margin:0 0 10px 0;font-size:13px;color:#666;">Los gastos del mes, por categoría.</p><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Plaza</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$130.000</strong><span style="color:#666;"> · 49%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="49%" bgcolor="#ffb775" style="background-color:#ffb775;font-size:0;line-height:0;">&nbsp;</td><td width="51%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Recursos</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$118.000</strong><span style="color:#666;"> · 44%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="44%" bgcolor="#b89bff" style="background-color:#b89bff;font-size:0;line-height:0;">&nbsp;</td><td width="56%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Internet</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$19.980</strong><span style="color:#666;"> · 7%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="7%" bgcolor="#6fd3e0" style="background-color:#6fd3e0;font-size:0;line-height:0;">&nbsp;</td><td width="93%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr></table><h2 style="margin:26px 0 2px 0;font-size:16px;font-weight:700;">De dónde vino la plata</h2><p style="margin:0 0 10px 0;font-size:13px;color:#666;">Lo que ingresó al barrio durante el mes.</p><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Cuotas</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$280.000</strong><span style="color:#666;"> · 79%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="79%" bgcolor="#5fd9b1" style="background-color:#5fd9b1;font-size:0;line-height:0;">&nbsp;</td><td width="21%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Venta de recursos</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$69.000</strong><span style="color:#666;"> · 20%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="20%" bgcolor="#7ab8ff" style="background-color:#7ab8ff;font-size:0;line-height:0;">&nbsp;</td><td width="80%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr><tr><td style="padding:7px 0 0 0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td style="font-size:13px;color:#222;">Intereses</td><td align="right" style="font-size:13px;white-space:nowrap;"><strong>$4.353</strong><span style="color:#666;"> · 1%</span></td></tr></table><div style="height:4px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-radius:3px;overflow:hidden;"><tr><td width="2%" bgcolor="#b89bff" style="background-color:#b89bff;font-size:0;line-height:0;">&nbsp;</td><td width="98%" bgcolor="#ededed" style="background-color:#ededed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr></table><h2 style="margin:26px 0 2px 0;font-size:16px;font-weight:700;">Hasta cuándo alcanza lo que hay</h2><div style="height:8px;font-size:0;line-height:0;">&nbsp;</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="76" bgcolor="#FF7A00" style="background-color:#FF7A00;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="64" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="52" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="40" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="28" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="16" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td><td valign="bottom" height="76" style="padding:0;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td height="4" bgcolor="#f6c99a" style="background-color:#f6c99a;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr><tr><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;font-weight:700;">Ago</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Sep</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Oct</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Nov</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Dic</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Ene</td><td align="center" style="padding:5px 0 0 0;font-size:10px;color:#666;">Feb</td></tr></table><p style="margin:10px 0 0 0;font-size:13px;color:#666;">A este ritmo, quedarían <strong>$92.687</strong> en febrero.</p><h2 style="margin:26px 0 2px 0;font-size:16px;font-weight:700;">Notas del cierre</h2><ul style="margin:0;padding-left:20px;font-size:14px;line-height:1.6;"><li style="margin:0 0 7px 0;">Al cierre del mes 34 casas tenían cuotas pendientes, por un total de $975.000.</li><li style="margin:0 0 7px 0;">El mes cerró con un superávit de $85.373: ingresó más de lo que se gastó.</li><li style="margin:0 0 7px 0;">12 casas salieron de mora respecto del mes anterior.</li><li style="margin:0 0 7px 0;">El mayor gasto del mes fue Plaza ($130.000).</li></ul><div style="margin:24px 0 0 0;padding-top:16px;border-top:1px solid #e5e5e5;font-size:13px;color:#666;">Este informe se envía todos los meses con el cierre del mes anterior. Si tienes dudas sobre alguna cifra, puedes escribirle a la Comisión.</div>\n' +
    '            </td>\n' +
    '          </tr>\n' +
    '          <tr>\n' +
    '            <td style="background-color:#000000;padding:16px 32px;font-size:12px;color:#bbbbbb;">\n' +
    '              Comisión Barrio Calbucura\n' +
    '            </td>\n' +
    '          </tr>\n' +
    '        </table>\n' +
    '      </td>\n' +
    '    </tr>\n' +
    '  </table>\n' +
    '</body>\n' +
    '</html>',
  texto: 'Versión texto plano del informe (se envía junto al HTML).',
  destinatarios: {
    total_casas: 71,
    titulares_con_correo: 45,
    casas_sin_correo: 3,
    identificadores_sin_correo: ['9001', '9002', '9003'],
    ya_enviados: 0,
  },
  // El preview muestra el envío habilitado para ver el flujo completo; en
  // producción el default es false (convención #19).
  envio_habilitado: true,
};

/** Recordatorio semestral con controles para revisar sus estados: seguro
 *  (cerrado / manual / automático) y temporada. Datos inventados (#21). */
function RecordatorioPreview() {
  const [modo, setModo] = useState<ModoEnvioRecordatorio>('cerrado');
  const [temporada, setTemporada] = useState(true);
  const mock: RecordatorioSemestralResponse = {
    periodo: '2027-S1',
    cuota_etiqueta: '1° semestre 2027',
    en_temporada: temporada,
    modo_envio: modo,
    dia_automatico: 5,
    cobertura: {
      casas_con_saldo: 25,
      con_correo: 14,
      identificadores_sin_correo: ['9001', '9002', '9003', '9004'],
      identificadores_sin_avisos: [],
      ya_enviados: 0,
      avisadas_hoy: 0,
    },
    asunto: RECORDATORIO_ASUNTO_MOCK,
    html: RECORDATORIO_HTML_MOCK,
    texto: 'Versión texto plano del recordatorio.',
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center text-xs text-text-secondary border border-dashed border-white/20 rounded-md p-2">
        <span>Preview:</span>
        <select
          value={modo}
          onChange={(e) => setModo(e.target.value as ModoEnvioRecordatorio)}
          className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-2 py-1"
        >
          <option value="cerrado">Seguro cerrado</option>
          <option value="manual">Manual</option>
          <option value="automatico">Automático</option>
        </select>
        <label className="inline-flex items-center gap-1">
          <input
            type="checkbox"
            checked={temporada}
            onChange={(e) => setTemporada(e.target.checked)}
          />
          Junio / diciembre
        </label>
      </div>
      <RecordatorioSemestral
        recordatorio={mock}
        // El preview no manda correos: confirma el flujo de la UI sin red.
        onEnviarPrueba={async () => ({
          enviado_a: 'tesorero@ejemplo.cl',
          ok: true,
          motivo: null,
        })}
        onEnviar={async () => ({
          periodo: '2027-S1',
          enviados: 14,
          omitidos: 0,
          fallidos: [],
        })}
      />
    </div>
  );
}

/** Cierre semestral con control del estado del próximo cierre. Datos
 *  inventados (#21); generar no llama a la API. */
function CierreSemestralPreview() {
  const [disponible, setDisponible] = useState(false);
  const [sinCierres, setSinCierres] = useState(false);
  const [tesorero, setTesorero] = useState(true);
  const estado: EstadoCierres = {
    periodo: '2027-S1',
    etiqueta: '1° semestre 2027',
    disponible_desde: '2027-07-01',
    disponible,
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center text-xs text-text-secondary border border-dashed border-white/20 rounded-md p-2">
        <span>Preview:</span>
        <label className="inline-flex items-center gap-1">
          <input
            type="checkbox"
            checked={disponible}
            onChange={(e) => setDisponible(e.target.checked)}
          />
          Semestre terminado
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="checkbox"
            checked={sinCierres}
            onChange={(e) => setSinCierres(e.target.checked)}
          />
          Sin cierres
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="checkbox"
            checked={tesorero}
            onChange={(e) => setTesorero(e.target.checked)}
          />
          Soy tesorero
        </label>
      </div>
      <CierreSemestralDetalleView
        datos={{ estado, cierres: sinCierres ? [] : CIERRES_MOCK }}
        puedeGenerar={tesorero}
        cargarDetalle={async (id) => DETALLES_MOCK[id]}
        onGenerar={async () => {
          throw new Error('La preview no genera cierres.');
        }}
        onGenerado={() => {}}
      />
    </div>
  );
}

export function TesoreriaReportesPreview() {
  const [sel, setSel] = useState<string | null>(null);

  const TITULOS: Record<string, string> = {
    morosidad: 'Estado de morosidad',
    'ingresos-gastos': 'Ingresos vs gastos',
    'gastos-categoria': 'Gastos por categoría',
    compensaciones: 'Estado de compensaciones',
    conciliacion: 'Conciliación bancaria',
    proveedores: 'Análisis de proveedores',
    'cierre-mensual': 'Cierre mensual',
    'informe-vecinos': 'Informe mensual a los vecinos',
    'recordatorio-semestral': 'Recordatorio semestral',
    'resumen-anual': 'Resumen anual',
    'cierre-semestral': 'Cierre semestral',
    'flujo-caja': 'Flujo de caja',
    'fondo-reserva': 'Fondo de reserva',
    proyeccion: 'Proyección 12 meses',
    recaudacion: 'Recaudación por casa',
  };

  if (sel) {
    return (
      <div className="area-imprimible max-w-5xl mx-auto px-4 py-6 space-y-4">
        <RepHeader
          onVolver={() => setSel(null)}
          titulo={TITULOS[sel] ?? sel}
          bajada="Vista de previsualización con datos de ejemplo (dev)."
          onExcel={() => {}}
        />
        {sel === 'morosidad' && <MorosidadDetalle reporte={MOROSIDAD_MOCK} />}
        {sel === 'ingresos-gastos' && (
          <IngresosGastosDetalle reporte={INGRESOS_GASTOS_MOCK} />
        )}
        {sel === 'gastos-categoria' && (
          <GastosCategoriaDetalle reporte={GASTOS_CATEGORIA_MOCK} />
        )}
        {sel === 'compensaciones' && (
          <CompensacionesDetalle comps={COMPENSACIONES_MOCK} />
        )}
        {sel === 'conciliacion' && (
          <ConciliacionDetalle reporte={CONCILIACION_MOCK} />
        )}
        {sel === 'proveedores' && (
          <ProveedoresDetalle reporte={PROVEEDORES_MOCK} />
        )}
        {sel === 'cierre-mensual' && (
          <CierreMensualDetalle reporte={CIERRE_MENSUAL_MOCK} />
        )}
        {sel === 'informe-vecinos' && (
          <InformeMensualVecinos
            informe={INFORME_VECINOS_MOCK}
            // El preview no manda correos: confirma el flujo de la UI sin red.
            onEnviarPrueba={async () => ({
              periodo: '2026-08',
              enviado_a: 'tesorero@ejemplo.cl',
              ok: true,
            })}
            onEnviar={async () => ({
              periodo: '2026-08',
              enviados: 45,
              omitidos_ya_enviados: 0,
              fallidos: [],
            })}
          />
        )}
        {sel === 'recordatorio-semestral' && <RecordatorioPreview />}
        {sel === 'resumen-anual' && (
          <ResumenAnualDetalle reporte={RESUMEN_ANUAL_MOCK} />
        )}
        {sel === 'cierre-semestral' && <CierreSemestralPreview />}
        {sel === 'flujo-caja' && (
          <FlujoCajaDetalle reporte={FLUJO_CAJA_MOCK} />
        )}
        {sel === 'fondo-reserva' && (
          <FondoReservaDetalle reporte={FONDO_RESERVA_MOCK} />
        )}
        {sel === 'proyeccion' && (
          <ProyeccionDetalle reporte={PROYECCION_MOCK} />
        )}
        {sel === 'recaudacion' && (
          <RecaudacionDetalle reporte={RECAUDACION_MOCK} />
        )}
      </div>
    );
  }

  return <CatalogoReportesView onVer={(id) => setSel(id)} />;
}
